import React, {
  useMemo,
  useState,
  useCallback,
  useEffect,
} from 'react';
import { useSelector } from 'react-redux';
import { Drawer, Divider, Table } from 'antd';
import { FormHelpers } from 'ontraccr-common';

import DrawerSubmitFooter from '../../common/containers/DrawerSubmitFooter';

import WorkflowConfigureSourceSelector from './selectors/WorkflowConfigureSourceSelector';

import { getSourceFieldMap, updateData } from './workflowHelpers';
import WorkflowSectionMappingConfigure from './WorkflowSectionMappingConfigure';
import OnTraccrCheckbox from '../../common/inputs/OnTraccrCheckbox';
import HoverHelp from '../../common/HoverHelp';
import RelativeDateRuleEditor from './RelativeDateRuleEditor';
import {
  getResetMaterialsAddedDateColumn,
  isMaterialsTableField,
} from '../MaterialsMappingColumns';

const {
  getDuplicationEnabledSections,
  getFieldsDisabledBySectionMappings,
} = FormHelpers;

const UNSUPPORTED_TYPES_FOR_DETECTION = new Set([
  'table',
  'calculation',
  'attachment',
  'multiSig',
  'weather',
  'timeTrackingGrid',
  'dynamicAttribute',
  'gpsLocation',
  'attribute',
  'payment',
  'staticAttachments',
  'staticText',
]);

const NON_DETECTABLE_SOURCE_IDS = new Set(['formNumber', 'cardNumber']);

const TEXT_SOURCE_TYPES = new Set(['text', 'staticText']);

const MIN_DRAWER_WIDTH = 1000;
const BASE_COLUMNS_DRAWER_WIDTH = 660;

// Multi-type targets (e.g. Title) only search as text when mapped from a text source.
const isTextDetectionTarget = (record, sourceType) => {
  if (record?.type) return record.type === 'text';
  return !!record?.types?.includes('text') && TEXT_SOURCE_TYPES.has(sourceType);
};

export default function WorkflowSimpleMappingConfigureDrawer({
  visible,
  onClose,
  onSubmit,
  sourceSections = [],
  fieldMappings = {},
  onFieldMappingsChange,
  sectionMappings = {},
  onSectionMappingsChange,
  shouldAllowDuplicateSectionMapping = false,
  fields = [],
  targetSections = [],
  fieldRenderMap = {},
  requiredFields = new Set(),
  title,
  targetHeader,
  detectDuplicates = false,
  updateExistingRowsOnly = false,
  canExcludeEmptyQuantity = false,
  canResetMaterialsAddedDate = false,
  // When true, aggregate all dropdowns under key `dropdown` (see getSourceFieldMap mapDropdown).
  mapDropdown = false,
  id,
  data = {},
  setDataMap,
  headerAction,
  renderTargetField,
}) {
  const currentUser = useSelector((state) => state.profile.profile);

  const isAdmin = currentUser.position === 'Admin';

  const sourceFieldMap = useMemo(
    () => getSourceFieldMap(sourceSections, null, [], { mapDropdown }),
    [sourceSections, mapDropdown],
  );

  const disabledSectionFieldIds = useMemo(() => {
    if (!shouldAllowDuplicateSectionMapping) return new Set();
    return getFieldsDisabledBySectionMappings(sectionMappings, targetSections);
  }, [shouldAllowDuplicateSectionMapping, sectionMappings, targetSections]);

  const onSectionMappingsChangeInner = useCallback((nextMappings) => {
    if (!onSectionMappingsChange) return;
    onSectionMappingsChange(nextMappings);

    // Update the field mappings to remove the disabled fields
    const nextDisabled = getFieldsDisabledBySectionMappings(nextMappings, targetSections);
    if (!nextDisabled.size) return;
    const nextFieldMappings = { ...fieldMappings };
    let changed = false;
    nextDisabled.forEach((fieldId) => {
      if (nextFieldMappings[fieldId]) {
        delete nextFieldMappings[fieldId];
        changed = true;
      }
    });
    if (changed) onFieldMappingsChange(nextFieldMappings);
  }, [
    fieldMappings,
    onFieldMappingsChange,
    onSectionMappingsChange,
    targetSections,
  ]);

  const [useToDetect, setUseToDetect] = useState(data.useToDetect || {});
  const [showInDetection, setShowInDetection] = useState(data.showInDetection || {});

  const { sourceTypeById, sourceTitleById } = useMemo(() => {
    const typeMap = {};
    const titleMap = {};
    sourceSections.forEach(({ fields: sectionFields = [], name: sectionName }) => {
      sectionFields?.forEach((field) => {
        if (!field?.id) return;
        typeMap[field.id] = field.selectedType;
        const fieldTitle = field.configProps?.title;
        titleMap[field.id] = sectionName ? `${sectionName} - ${fieldTitle}` : fieldTitle;
      });
    });
    return { sourceTypeById: typeMap, sourceTitleById: titleMap };
  }, [sourceSections]);

  const isDetectableSource = useCallback((record, detectState) => {
    const sourceId = fieldMappings[record.key];
    return !!sourceId
      && !NON_DETECTABLE_SOURCE_IDS.has(sourceId)
      && !!detectState[record.key]?.enabled;
  }, [fieldMappings]);

  const canIgnoreCaseInDetection = useCallback((record, detectState) => (
    isDetectableSource(record, detectState)
    && isTextDetectionTarget(record, sourceTypeById[fieldMappings[record.key]])
  ), [isDetectableSource, fieldMappings, sourceTypeById]);

  const canUseRelativeDateInDetection = useCallback((record, detectState) => (
    isDetectableSource(record, detectState) && record.type === 'dateTime'
  ), [isDetectableSource]);

  // Update the change handlers
  const onUseToDetectChange = useCallback((record, checked) => {
    const newUseToDetect = {
      ...useToDetect,
      [record.key]: { enabled: checked, record },
    };
    setUseToDetect(newUseToDetect);
    setDataMap(updateData(id, { useToDetect: newUseToDetect }));
  }, [useToDetect, setDataMap, id]);

  const onCaseInsensitiveDetectionChange = useCallback((record, checked) => {
    const newUseToDetect = {
      ...useToDetect,
      [record.key]: { ...useToDetect[record.key], caseInsensitive: checked },
    };
    setUseToDetect(newUseToDetect);
    setDataMap(updateData(id, { useToDetect: newUseToDetect }));
  }, [useToDetect, setDataMap, id]);

  const onRelativeDateDetectionChange = useCallback((record, relativeDate) => {
    const { relativeDate: previous, ...entry } = useToDetect[record.key] ?? {};
    const newUseToDetect = {
      ...useToDetect,
      [record.key]: relativeDate ? { ...entry, relativeDate } : entry,
    };
    setUseToDetect(newUseToDetect);
    setDataMap(updateData(id, { useToDetect: newUseToDetect }));
  }, [useToDetect, setDataMap, id]);

  const onShowInDetectionChange = useCallback((record, checked) => {
    const newShowInDetection = {
      ...showInDetection,
      [record.key]: { enabled: checked, record },
    };
    setShowInDetection(newShowInDetection);
    setDataMap(updateData(id, { showInDetection: newShowInDetection }));
  }, [showInDetection, setDataMap, id]);

  useEffect(() => {
    setUseToDetect((prev) => {
      let changed = false;
      const next = { ...prev };

      fields.forEach((record) => {
        if (
          NON_DETECTABLE_SOURCE_IDS.has(fieldMappings[record.key])
          && next[record.key]?.enabled
        ) {
          next[record.key] = { ...next[record.key], enabled: false };
          changed = true;
        }
        // Source remapped/unmapped, or target/source no longer text
        if (next[record.key]?.caseInsensitive && !canIgnoreCaseInDetection(record, next)) {
          next[record.key] = { ...next[record.key], caseInsensitive: false };
          changed = true;
        }
        // Source unmapped, or target no longer a date
        if (next[record.key]?.relativeDate && !canUseRelativeDateInDetection(record, next)) {
          const { relativeDate, ...entry } = next[record.key];
          next[record.key] = entry;
          changed = true;
        }
      });

      if (changed) {
        setDataMap(updateData(id, { useToDetect: next }));
        return next;
      }
      return prev;
    });

    setShowInDetection((prev) => {
      let changed = false;
      const next = { ...prev };

      fields.forEach((record) => {
        if (
          NON_DETECTABLE_SOURCE_IDS.has(fieldMappings[record.key])
          && next[record.key]?.enabled
        ) {
          next[record.key] = { ...next[record.key], enabled: false };
          changed = true;
        }
      });

      if (changed) {
        setDataMap(updateData(id, { showInDetection: next }));
        return next;
      }
      return prev;
    });
  }, [
    fieldMappings,
    fields,
    id,
    setDataMap,
    canIgnoreCaseInDetection,
    canUseRelativeDateInDetection,
  ]);

  const renderDetectionCheckbox = useCallback((record, stateObj, onChange) => {
    if (UNSUPPORTED_TYPES_FOR_DETECTION.has(record.type)) {
      return null;
    }
    const hasSourceField = !!fieldMappings[record.key];
    const isNonDetectable = NON_DETECTABLE_SOURCE_IDS.has(fieldMappings[record.key]);
    const isEnabled = stateObj[record.key]?.enabled || false;

    return (
      <OnTraccrCheckbox
        value={hasSourceField && !isNonDetectable ? isEnabled : false}
        onChange={(checked) => onChange(record, checked)}
        disabled={!hasSourceField || isNonDetectable}
      />
    );
  }, [fieldMappings]);

  const updateExistingTableRows = useMemo(() => data.updateExistingTableRows || {}, [data]);

  const hasMaterialsTable = useMemo(
    () => fields.some((f) => isMaterialsTableField(f)),
    [fields],
  );

  const onUpdateExistingTableRowsChange = useCallback((record, checked) => {
    const newUpdateExistingTableRows = {
      ...updateExistingTableRows,
      [record.key]: { enabled: checked, record },
    };
    setDataMap(updateData(id, { updateExistingTableRows: newUpdateExistingTableRows }));
  }, [updateExistingTableRows, setDataMap, id]);

  const renderUpdateExistingTableRowsCheckbox = useCallback((record, stateObj, onChange) => {
    if (!isMaterialsTableField(record)) {
      return '—';
    }
    return (
      <OnTraccrCheckbox
        value={stateObj[record.key]?.enabled || false}
        onChange={(checked) => onChange(record, checked)}
      />
    );
  }, []);

  const excludeEmptyQuantityMaterialsRows = data.excludeEmptyQuantityMaterialsRows || {};

  const onExcludeEmptyQuantityChange = useCallback((record, checked) => {
    setDataMap(updateData(id, {
      excludeEmptyQuantityMaterialsRows: {
        ...excludeEmptyQuantityMaterialsRows,
        [record.key]: checked,
      },
    }));
  }, [excludeEmptyQuantityMaterialsRows, setDataMap, id]);

  const resetMaterialsAddedDate = data.resetMaterialsAddedDate || {};

  const onResetMaterialsAddedDateChange = useCallback((recordId, checked) => {
    setDataMap(updateData(id, {
      resetMaterialsAddedDate: {
        ...resetMaterialsAddedDate,
        [recordId]: checked,
      },
    }));
  }, [resetMaterialsAddedDate, setDataMap, id]);

  const baseColumns = [{
    dataIndex: 'sourceField',
    title: 'Source Field',
    width: 350,
    render: (_, record) => {
      const {
        key,
        type, // TODO: change all existing type keys to a types array
        types = [],
        dataType,
        subDataType,
        allowMultipleStaticText = false,
      } = record;

      let newType;
      let newTypeWithSubDataType;
      if (dataType) {
        newType = `${type} - ${dataType}`;
        newTypeWithSubDataType = `${type} - ${dataType} - ${subDataType}`;
      } else {
        newType = type;
      }

      const {
        [newType]: sourceFields = [],
        [newTypeWithSubDataType]: sourceFieldsWithSubDataType = [],
      } = sourceFieldMap;

      let eligibleSourceFields = sourceFields;

      if (types.length) {
        types.forEach((typeValue) => {
          const {
            [typeValue]: typeSourceFields = [],
          } = sourceFieldMap;

          eligibleSourceFields = eligibleSourceFields.concat(typeSourceFields);
        });
      }

      if (subDataType) {
        eligibleSourceFields = eligibleSourceFields.concat(sourceFieldsWithSubDataType);
      }

      const {
        [key]: defaultValue,
      } = fieldMappings;

      if (disabledSectionFieldIds.has(key)) {
        return (
          <span style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#8c8c8c' }}>
            Disabled
            <HoverHelp
              content={(
                <div style={{ maxWidth: 280 }}>
                  This field belongs to a duplicated section that is already mapped
                  in Duplicated Section Mapping. Map it there instead of the general
                  field mapping.
                </div>
              )}
            />
          </span>
        );
      }

      if (key in fieldRenderMap) {
        return fieldRenderMap[key]({
          fieldMappings,
          sourceFieldMap,
          defaultValue,
          onFieldMappingsChange,
        });
      }

      return (
        <WorkflowConfigureSourceSelector
          fieldMappings={fieldMappings}
          defaultValue={defaultValue}
          onFieldMappingsChange={onFieldMappingsChange}
          targetId={key}
          eligibleSourceFields={eligibleSourceFields}
          allowMultipleStaticText={allowMultipleStaticText}
        />
      );
    },
  }, {
    dataIndex: 'text',
    title: targetHeader,
    render: (_, record) => {
      if (renderTargetField) return renderTargetField(record);
      const className = requiredFields.has(record.key) ? 'form-required-field' : null;
      return <span className={className}>{record.text}</span>;
    },
  }];

  const additionalColumns = [];
  if (detectDuplicates) {
    additionalColumns.push({
      dataIndex: 'useToDetectDuplicates',
      title: (
        <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          Use to detect duplicates
          <HoverHelp
            content={(
              <>
                Use this field to check for existing board cards with matching field values.
                <br />
                Note that all selected fields must be filled before detection can be performed.
              </>
            )}
            placement="top"
            iconProps={{ style: { fontSize: '14px', color: '#8c8c8c' } }}
          />
        </span>
      ),
      width: 200,
      render: (_, record) => renderDetectionCheckbox(record, useToDetect, onUseToDetectChange),
    }, {
      dataIndex: 'detectionRule',
      title: (
        <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          Detection rule
          <HoverHelp
            content={(
              <div style={{ maxWidth: 300 }}>
                How this field is compared with existing board cards.
                <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
                  <li>
                    <b>Text:</b>
                    {' '}
                    optionally ignore upper or lower case.
                  </li>
                  <li>
                    <b>Date:</b>
                    {' '}
                    same day as the form value, or a date relative to today or the form value.
                  </li>
                </ul>
              </div>
            )}
            placement="top"
            iconProps={{ style: { fontSize: '14px', color: '#8c8c8c' } }}
          />
        </span>
      ),
      width: 260,
      render: (_, record) => {
        if (record.type === 'dateTime') {
          return (
            <RelativeDateRuleEditor
              cardFieldLabel={record.text}
              sourceLabel={sourceTitleById[fieldMappings[record.key]]}
              value={useToDetect[record.key]?.relativeDate}
              disabled={!canUseRelativeDateInDetection(record, useToDetect)}
              onChange={(relativeDate) => onRelativeDateDetectionChange(record, relativeDate)}
            />
          );
        }
        const canBeText = record.type === 'text' || !!record.types?.includes('text');
        if (!canBeText) return null;
        const applicable = canIgnoreCaseInDetection(record, useToDetect);
        return (
          <OnTraccrCheckbox
            value={applicable && !!useToDetect[record.key]?.caseInsensitive}
            onChange={(checked) => onCaseInsensitiveDetectionChange(record, checked)}
            disabled={!applicable}
            label="Ignore case"
          />
        );
      },
    });
  }

  if (detectDuplicates && isAdmin) {
    additionalColumns.push({
      dataIndex: 'showInDetection',
      title: (
        <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          Show in detection
          <HoverHelp
            content={<>Display this field in the detection results.</>}
            placement="top"
            iconProps={{ style: { fontSize: '14px', color: '#8c8c8c' } }}
          />
        </span>
      ),
      width: 200,
      render: (_, record) => renderDetectionCheckbox(
        record,
        showInDetection,
        onShowInDetectionChange,
      ),
    });
  }

  if (updateExistingRowsOnly && hasMaterialsTable) {
    additionalColumns.push({
      dataIndex: 'updateExistingRowsOnly',
      title: (
        <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          Update existing rows only?
          <HoverHelp
            content={(
              <div style={{ width: 300 }}>
                When checked, this <strong>materials table</strong> field will only update existing
                rows that originally come from the <strong>board card table</strong>. New rows added
                into the table will not be appended to the board card table.
              </div>
            )}
          />
        </span>
      ),
      width: 150,
      render: (_, record) => renderUpdateExistingTableRowsCheckbox(
        record,
        updateExistingTableRows,
        onUpdateExistingTableRowsChange,
      ),
    });
  }

  if (canExcludeEmptyQuantity && hasMaterialsTable) {
    additionalColumns.push({
      dataIndex: 'canExcludeEmptyQuantity',
      title: (
        <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          Exclude empty quantity materials?
          <HoverHelp
            content={(
              <div style={{ width: 300 }}>
                Exclude rows in a materials table where <strong>Quantity</strong> is blank or zero.
              </div>
            )}
          />
        </span>
      ),
      width: 150,
      render: (_, record) => {
        if (!isMaterialsTableField(record)) {
          return '—';
        }
        return (
          <OnTraccrCheckbox
            value={!!excludeEmptyQuantityMaterialsRows[record.key]}
            onChange={(checked) => onExcludeEmptyQuantityChange(record, checked)}
          />
        );
      },
    });
  }

  if (canResetMaterialsAddedDate && hasMaterialsTable) {
    additionalColumns.push(getResetMaterialsAddedDateColumn({
      valueMap: resetMaterialsAddedDate,
      onChange: onResetMaterialsAddedDateChange,
      getRecordId: (record) => record.key,
    }));
  }

  const columns = [...baseColumns, ...additionalColumns];

  const drawerWidth = Math.max(
    MIN_DRAWER_WIDTH,
    additionalColumns.reduce((sum, column) => sum + (column.width ?? 0), BASE_COLUMNS_DRAWER_WIDTH),
  );

  const hasSectionMapping = shouldAllowDuplicateSectionMapping
    && getDuplicationEnabledSections(sourceSections).length > 0
    && getDuplicationEnabledSections(targetSections).length > 0;

  return (
    <Drawer
      title="Configure Field Mapping"
      width={`min(${drawerWidth}px, 95vw)`}
      onClose={onClose}
      visible={visible}
      bodyStyle={hasSectionMapping ? { paddingBottom: 60 } : undefined}
    >
      {title}
      {headerAction}
      <Divider style={{ marginBottom: shouldAllowDuplicateSectionMapping ? 16 : 0 }} />
      {shouldAllowDuplicateSectionMapping && (
        <WorkflowSectionMappingConfigure
          sourceSections={sourceSections}
          targetSections={targetSections}
          sectionMappings={sectionMappings}
          onSectionMappingsChange={onSectionMappingsChangeInner}
        />
      )}
      <Table
        dataSource={fields}
        columns={columns}
        pagination={false}
        size="small"
        scroll={hasSectionMapping ? undefined : { y: 'calc(100vh - 218px)' }}
        rowKey={(record) => record.id ?? record.key}
      />
      <DrawerSubmitFooter
        onClose={onClose}
        onSubmit={onSubmit}
      />
    </Drawer>
  );
}
